import React, { useEffect, useState } from 'react';
import { ActionButton } from './ActionButton';
import { MID_GRAY } from '@/styles/tokens';
import { DeviceProviderDrilldown } from './DeviceProviderDrilldown';

export interface GaugeContext {
  componentId: string;
  title: string;
  gaugeType: string;
  displayMode: 'dial' | 'ring' | 'bar' | 'milestone';
  value: number;
  min: number;
  max: number;
  unit: string;
  hasDataSource: boolean;
  thresholds: Array<{
    min: number;
    max: number;
    color: string;
    label?: string;
  }>;
}

interface GaugeActionCardProps {
  gaugeContext: GaugeContext;
  homeAssistantSummary?: string | null;
  homeAssistantButtonLabel?: string;
  hideComponentLabel?: string;
  onAutoSend: (command: string) => void;
  onPreFill: (text: string) => void;
  onDataDiscovery: () => void;
  onDeviceBinding?: () => void;
  onHomeAssistantBinding?: () => void;
  onHomeAssistantUnbind?: () => void;
  onHomeAssistantExplain?: () => void;
  onDuplicateComponent?: () => void;
  onHideComponent?: () => void;
  onUpdateGauge?: (updates: {
    min: number;
    max: number;
    thresholds: GaugeContext['thresholds'];
  }) => void;
}

type GaugeCategory = 'range' | 'unit' | 'mode' | 'segments';
type GaugeDrilldown = GaugeCategory | 'deviceProvider';
type DisplayModeOption = {
  key: GaugeContext['displayMode'];
  label: string;
};

const RANGE_PRESETS = [
  { label: '0\u2013100', cmd: 'set range 0 to 100' },
  { label: '0\u20131000', cmd: 'set range 0 to 1000' },
  { label: '0\u201350', cmd: 'set range 0 to 50' },
  { label: '-50\u201350', cmd: 'set range -50 to 50' },
];

const UNITS = ['%', '\u00b0C', '\u00b0F', 'PSI', 'kW', 'V', 'A', 'pH', 'RPM'];

const DISPLAY_MODES: DisplayModeOption[] = [
  { key: 'dial', label: 'Dial' },
  { key: 'ring', label: 'Ring' },
  { key: 'bar', label: 'Bar' },
  { key: 'milestone', label: 'Milestone' },
];

export const GaugeActionCard: React.FC<GaugeActionCardProps> = ({
  gaugeContext,
  homeAssistantSummary,
  homeAssistantButtonLabel = 'Device',
  hideComponentLabel = 'Hide',
  onAutoSend,
  onPreFill,
  onDataDiscovery,
  onDeviceBinding,
  onHomeAssistantBinding,
  onHomeAssistantUnbind,
  onHomeAssistantExplain,
  onDuplicateComponent,
  onHideComponent,
  onUpdateGauge,
}) => {
  const [expandedCategory, setExpandedCategory] = useState<GaugeDrilldown | null>(null);
  const [segmentDrafts, setSegmentDrafts] = useState<GaugeContext['thresholds']>(gaugeContext.thresholds ?? []);
  const [rangeDraft, setRangeDraft] = useState({ min: gaugeContext.min, max: gaugeContext.max });
  const thresholdSignature = JSON.stringify(gaugeContext.thresholds ?? []);

  useEffect(() => {
    setSegmentDrafts(gaugeContext.thresholds ?? []);
  }, [gaugeContext.componentId, thresholdSignature]);

  useEffect(() => {
    setRangeDraft({ min: gaugeContext.min, max: gaugeContext.max });
  }, [gaugeContext.componentId, gaugeContext.min, gaugeContext.max]);

  const updateSegmentDraft = (index: number, key: 'min' | 'max', value: string) => {
    setSegmentDrafts((prev) =>
      prev.map((segment, segmentIndex) =>
        segmentIndex === index
          ? { ...segment, [key]: Number(value) || 0 }
          : segment
      )
    );
  };

  const updateRangeDraft = (key: 'min' | 'max', value: string) => {
    setRangeDraft((prev) => ({
      ...prev,
      [key]: Number(value) || 0,
    }));
  };

  const applyGaugeUpdates = () => {
    if (!onUpdateGauge) return;

    const thresholdMins = segmentDrafts.map((segment) => segment.min);
    const thresholdMaxes = segmentDrafts.map((segment) => segment.max);
    const nextMin = thresholdMins.length > 0
      ? Math.min(rangeDraft.min, ...thresholdMins)
      : rangeDraft.min;
    const nextMax = thresholdMaxes.length > 0
      ? Math.max(rangeDraft.max, ...thresholdMaxes)
      : rangeDraft.max;

    onUpdateGauge({
      min: nextMin,
      max: nextMax,
      thresholds: segmentDrafts,
    });
  };

  const openDeviceBindingFlow = () => {
    if (onHomeAssistantBinding && onDeviceBinding) {
      setExpandedCategory('deviceProvider');
      return;
    }
    if (onHomeAssistantBinding) {
      onHomeAssistantBinding();
      return;
    }
    onDeviceBinding?.();
  };

  return (
    <>
      <p style={{ fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
        ◎ <strong>{gaugeContext.gaugeType}: &quot;{gaugeContext.title}&quot;</strong>
      </p>
      <p style={{ fontSize: '12px', margin: '4px 0 8px', color: MID_GRAY.DEFAULT }}>
        {homeAssistantSummary ||
          (gaugeContext.hasDataSource
            ? 'Live data connected'
            : `Value: ${gaugeContext.value} | Range: ${gaugeContext.min}\u2013${gaugeContext.max}${gaugeContext.unit ? ` | Unit: ${gaugeContext.unit}` : ''}`)}
      </p>

      {expandedCategory === 'deviceProvider' ? (
        <DeviceProviderDrilldown
          onBack={() => setExpandedCategory(null)}
          onHomeAssistantBinding={onHomeAssistantBinding}
          onDeviceBinding={onDeviceBinding}
        />
      ) : expandedCategory === 'range' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px' }}>
            <ActionButton variant="back" onClick={() => setExpandedCategory(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>Set Range</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {RANGE_PRESETS.map((preset) => (
              <ActionButton key={preset.label} onClick={() => onAutoSend(preset.cmd)}>
                {preset.label}
              </ActionButton>
            ))}
            <ActionButton
              onClick={() => {
                onPreFill('set range ');
                setExpandedCategory(null);
              }}
            >
              Custom…
            </ActionButton>
          </div>
        </>
      ) : expandedCategory === 'unit' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px' }}>
            <ActionButton variant="back" onClick={() => setExpandedCategory(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>Set Unit</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {UNITS.map((unit) => (
              <ActionButton
                key={unit}
                variant={gaugeContext.unit === unit ? 'active' : 'default'}
                onClick={() => onAutoSend(`set unit to ${unit}`)}
              >
                {gaugeContext.unit === unit ? `✓ ${unit}` : unit}
              </ActionButton>
            ))}
            <ActionButton
              onClick={() => {
                onPreFill('set unit to ');
                setExpandedCategory(null);
              }}
            >
              Custom…
            </ActionButton>
          </div>
        </>
      ) : expandedCategory === 'mode' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px' }}>
            <ActionButton variant="back" onClick={() => setExpandedCategory(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>Display Mode</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {DISPLAY_MODES.map((mode) => (
              <ActionButton
                key={mode.key}
                variant={gaugeContext.displayMode === mode.key ? 'active' : 'default'}
                onClick={() => onAutoSend(`switch to ${mode.key}`)}
              >
                {gaugeContext.displayMode === mode.key ? `✓ ${mode.label}` : mode.label}
              </ActionButton>
            ))}
          </div>
        </>
      ) : expandedCategory === 'segments' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 8px' }}>
            <ActionButton variant="back" onClick={() => setExpandedCategory(null)}>
              ← Back
            </ActionButton>
            <span style={{ fontSize: '12px', color: MID_GRAY.DEFAULT }}>Edit Segments</span>
          </div>
          {(segmentDrafts.length === 0 || !onUpdateGauge) ? (
            <p style={{ fontSize: '12px', margin: 0, color: MID_GRAY.DEFAULT }}>
              No threshold segments are configured for this gauge yet.
            </p>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '10px',
                }}
              >
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: MID_GRAY.DEFAULT }}>
                  Gauge Min
                  <input
                    type="number"
                    value={rangeDraft.min}
                    onChange={(e) => updateRangeDraft('min', e.target.value)}
                    style={{
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: '6px',
                      padding: '6px 8px',
                      fontSize: '12px',
                      color: 'inherit',
                      background: 'white',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: MID_GRAY.DEFAULT }}>
                  Gauge Max
                  <input
                    type="number"
                    value={rangeDraft.max}
                    onChange={(e) => updateRangeDraft('max', e.target.value)}
                    style={{
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: '6px',
                      padding: '6px 8px',
                      fontSize: '12px',
                      color: 'inherit',
                      background: 'white',
                    }}
                  />
                </label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                {segmentDrafts.map((segment, index) => (
                  <div
                    key={`${segment.label || 'segment'}-${index}`}
                    style={{
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '999px',
                          backgroundColor: segment.color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>
                        {segment.label || `Segment ${index + 1}`}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: MID_GRAY.DEFAULT }}>
                        Min
                        <input
                          type="number"
                          value={segment.min}
                          onChange={(e) => updateSegmentDraft(index, 'min', e.target.value)}
                          style={{
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            color: 'inherit',
                            background: 'white',
                          }}
                        />
                      </label>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', color: MID_GRAY.DEFAULT }}>
                        Max
                        <input
                          type="number"
                          value={segment.max}
                          onChange={(e) => updateSegmentDraft(index, 'max', e.target.value)}
                          style={{
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: '6px',
                            padding: '6px 8px',
                            fontSize: '12px',
                            color: 'inherit',
                            background: 'white',
                          }}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '11px', margin: '0 0 10px', color: MID_GRAY.DEFAULT }}>
                Apply updates the gauge range too. If a segment extends beyond the current range, the gauge max or min expands to match it.
              </p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <ActionButton
                  onClick={applyGaugeUpdates}
                >
                  Apply Segments
                </ActionButton>
                <ActionButton
                  variant="dashed"
                  onClick={() => {
                    setSegmentDrafts(gaugeContext.thresholds ?? []);
                    setRangeDraft({ min: gaugeContext.min, max: gaugeContext.max });
                  }}
                >
                  Reset
                </ActionButton>
              </div>
            </>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <ActionButton onClick={onDataDiscovery}>
            Add Data
          </ActionButton>
          {(onHomeAssistantBinding || onDeviceBinding) && (
            <ActionButton onClick={openDeviceBindingFlow}>
              {homeAssistantButtonLabel}
            </ActionButton>
          )}
          {onHomeAssistantUnbind && (
            <ActionButton variant="destructive" onClick={onHomeAssistantUnbind}>
              Remove Binding
            </ActionButton>
          )}
          {onHomeAssistantExplain && (
            <ActionButton onClick={onHomeAssistantExplain}>
              Explain Binding
            </ActionButton>
          )}
          <ActionButton onClick={() => setExpandedCategory('mode')}>
            Display Mode
          </ActionButton>
          <ActionButton onClick={() => setExpandedCategory('range')}>
            Set Range
          </ActionButton>
          {gaugeContext.displayMode !== 'milestone' && (gaugeContext.thresholds?.length ?? 0) > 0 && onUpdateGauge && (
            <ActionButton onClick={() => setExpandedCategory('segments')}>
              Edit Segments
            </ActionButton>
          )}
          <ActionButton onClick={() => setExpandedCategory('unit')}>
            Set Unit
          </ActionButton>
          {gaugeContext.displayMode === 'ring' && (
            <ActionButton onClick={() => onPreFill('add concentric rings for ')}>
              Add Concentric Rings
            </ActionButton>
          )}
          {gaugeContext.displayMode === 'bar' && (
            <ActionButton onClick={() => onPreFill('add stacked bar with ')}>
              Add Stacked Segments
            </ActionButton>
          )}
          {gaugeContext.displayMode === 'milestone' && (
            <ActionButton onClick={() => onPreFill('add milestones ')}>
              Configure Milestones
            </ActionButton>
          )}
          <ActionButton onClick={() => onAutoSend('make this selected component smaller')}>
            Smaller
          </ActionButton>
          <ActionButton onClick={() => onAutoSend('make this selected component larger')}>
            Larger
          </ActionButton>
          <ActionButton onClick={() => onAutoSend('align this selected component center')}>
            Center
          </ActionButton>
          <ActionButton onClick={() => onAutoSend('restyle this selected component for a Home Assistant dashboard')}>
            Style
          </ActionButton>
          {onDuplicateComponent && (
            <ActionButton onClick={onDuplicateComponent}>
              Duplicate
            </ActionButton>
          )}
          {onHideComponent && (
            <ActionButton onClick={onHideComponent}>
              {hideComponentLabel}
            </ActionButton>
          )}
          <ActionButton variant="destructive" onClick={() => onPreFill('delete this selected component')}>
            Delete
          </ActionButton>
        </div>
      )}
    </>
  );
};
